import React, { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { StickyNote, Plus, ExternalLink, Loader } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useLanguage } from '../../contexts/LanguageContext';
import CreateNoteModal from '../modals/CreateNoteModal';
import { notesAPI } from '../../utils/api';
import { format } from 'date-fns';
import { fr, enUS, es } from 'date-fns/locale';
import { getErrorMessage } from '../../utils/errorTranslation';

const NotesWidget = () => {
  const { t, currentLanguage } = useLanguage();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);

  const dateLocale = useMemo(() => {
    switch (currentLanguage) {
      case 'en':
        return enUS;
      case 'es':
        return es;
      default:
        return fr;
    }
  }, [currentLanguage]);

  const loadRecentNotes = async () => {
    try {
      setLoading(true);
      const response = await notesAPI.getRecent({ t: Date.now() });
      const data = response.data;
      setNotes(data.notes || []);
    } catch (error) {
      console.error('Error loading recent notes:', error);
      toast.error(getErrorMessage(error, t, t('dashboard.errors.notesLoadFailed', 'Error loading notes')));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecentNotes();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffHours < 1) {
      const diffMins = Math.max(1, Math.floor(diffMs / 60000));
      return t('dashboard.activity.time.minutesAgo')
        .replace('{{count}}', diffMins)
        .replace('{{plural}}', diffMins > 1 ? 's' : '');
    }
    if (diffHours < 24) {
      return t('dashboard.activity.time.hoursAgo')
        .replace('{{count}}', diffHours)
        .replace('{{plural}}', diffHours > 1 ? 's' : '');
    }
    if (diffDays < 7) {
      return t('dashboard.activity.time.daysAgo')
        .replace('{{count}}', diffDays)
        .replace('{{plural}}', diffDays > 1 ? 's' : '');
    }
    return format(date, 'P', { locale: dateLocale });
  };

  const truncateText = (text = '', maxLength = 80) => {
    if (text.length <= maxLength) return text;
    return `${text.slice(0, maxLength)}...`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="card h-full rounded-2xl border border-slate-200 shadow-sm"
    >
      <div className="card-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <StickyNote className="h-5 w-5 text-amber-600 mr-2" />
            <h3 className="text-lg font-medium text-gray-900">
              {t('dashboard.widgets.recentNotes')}
            </h3>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center text-xs text-primary-600 hover:text-primary-700"
            >
              <Plus className="h-4 w-4 mr-1" />
              {t('dashboard.widgets.addQuickNote')}
            </button>
            <Link
              to="/dashboard/notes"
              className="inline-flex items-center text-xs text-gray-500 hover:text-gray-700"
            >
              <ExternalLink className="h-4 w-4 mr-1" />
              {t('dashboard.widgets.viewAllNotes')}
            </Link>
          </div>
        </div>
      </div>

      <div className="card-content">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader className="h-6 w-6 text-gray-400 animate-spin" />
          </div>
        ) : notes.length === 0 ? (
          <div className="text-center text-sm text-gray-500 py-6">
            <p className="font-medium">{t('dashboard.widgets.noNotes')}</p>
            <p className="mt-1">{t('dashboard.widgets.createFirstNote')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {notes.map((note) => (
              <div key={note.id} className="border border-gray-100 rounded-xl p-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-gray-900">
                    {note.title || t('dashboard.widgets.recentNotes')}
                  </p>
                  <span className="text-xs text-gray-400">{formatDate(note.updated_at || note.created_at)}</span>
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  {truncateText(note.content || '')}
                </p>
                {note.note_type === 'shared' && (
                  <div className="mt-2 inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700">
                    {t('notes.card.shared')}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <CreateNoteModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={async () => {
          await new Promise(resolve => setTimeout(resolve, 100));
          await loadRecentNotes();
        }}
      />
    </motion.div>
  );
};

export default NotesWidget;
